import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  InputSignal,
  model,
  ModelSignal,
  Signal,
} from '@angular/core';
import { SectionRow } from '../section-row/section-row';
import { SectionEditorToolbar } from '../section-editor-toolbar/section-editor-toolbar';
import { SectionChange, SectionModelType } from '../../types/section.type';
import { WorkoutMode } from '@kinomap-dev/ng-ui-lib/core';
import { CdkDropList, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { SectionChart } from '../section-chart/section-chart';
import { CdkTableModule } from '@angular/cdk/table';
import { ColorService } from '../../services/color.service';
import { SectionsEditorService } from '../../services/sections-editor.service';
import {
  createDefaultCoachingSection,
  createDefaultWorkoutSection,
  generateSectionId,
} from '../../utils/default-section';
import { durationToSeconds, formatShortDuration } from '../../utils/duration.utils';
import { avgOf, maxOf, minOf } from '../../utils/sections-editor.utils';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { Keydown } from '../../directives/keydown';
import { HorizontalPosition } from '../../types/position.type';

import { TranslateModule } from '@ngx-translate/core';
type DataStat = { type: WorkoutMode; min?: number; avg?: number; max?: number };

@Component({
  selector: 'app-sections-editor',
  imports: [
    TranslateModule,
    SectionRow,
    SectionEditorToolbar,
    CdkDropList,
    SectionChart,
    IconComponent,
    CdkTableModule,
  ],
  templateUrl: './sections-editor.html',
  styleUrl: './sections-editor.scss',
  providers: [SectionsEditorService],
  hostDirectives: [Keydown],
  host: { '[class.error]': '!!errorMessage()' },
})
export class SectionsEditor {
  colorService: ColorService = inject(ColorService);
  sectionEditorService: SectionsEditorService = inject(SectionsEditorService);

  contentType: InputSignal<'coaching' | 'workout'> = input.required<'coaching' | 'workout'>();
  workoutMode: InputSignal<WorkoutMode | undefined> = input<WorkoutMode>();
  disabled: InputSignal<boolean> = input<boolean>(false);
  errorMessage: InputSignal<string | undefined> = input<string>();
  sections: ModelSignal<SectionModelType[]> = model.required<SectionModelType[]>();
  selectedSectionsIds: ModelSignal<number[]> = model<number[]>([]);

  constructor() {
    effect(() => this.sectionEditorService.workoutMode.set(this.workoutMode()));
    effect(() => this.sectionEditorService.contentType.set(this.contentType()));
    this.sectionEditorService.bindSections(this.sections);
    this.sectionEditorService.bindSelection(this.selectedSectionsIds);
    const host: HTMLElement = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    this.listenEscape(host);
    this.listenOutsideClick(host);
    inject(Keydown).keyPressed.subscribe((event: KeyboardEvent) => {
      if (this.isBehindDialog(host)) return;
      if (
        (event.key === 'Delete' || event.key === 'Backspace') &&
        this.selectedSections().length > 0 &&
        !this.isEditingText(event)
      ) {
        event.preventDefault();
        this.deleteSelectedSections();
      }
    });
  }

  private listenEscape(host: HTMLElement): void {
    const onKeydown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || this.isBehindDialog(host)) return;
      if (!this.selectedSections().length) return;
      event.stopPropagation();
      this.sectionEditorService.clearSectionSelection();
    };
    window.addEventListener('keydown', onKeydown, { capture: true });
    inject(DestroyRef).onDestroy(() =>
      window.removeEventListener('keydown', onKeydown, { capture: true }),
    );
  }

  /**
   * Un clic hors des segments, des rows et de la toolbar vide la selection.
   * En capture pour lire la cible avant qu'un handler ne la retire du DOM ;
   * les overlays CDK (menus, selects de la toolbar) comptent comme dedans, et
   * l'ouverture ou la fermeture du plein ecran conserve la selection.
   */
  private listenOutsideClick(host: HTMLElement): void {
    const onClick = (event: MouseEvent): void => {
      if (!this.selectedSections().length || this.isBehindDialog(host)) return;
      const target: HTMLElement | null = event.target as HTMLElement | null;
      if (
        target?.closest(
          '.section-block, .create-new-section, app-section-row, app-section-editor-toolbar, .cdk-overlay-container, .fullscreen-button, #content-modal > header',
        )
      ) {
        return;
      }
      this.sectionEditorService.clearSectionSelection();
    };

    document.addEventListener('click', onClick, { capture: true });
    inject(DestroyRef).onDestroy(() =>
      document.removeEventListener('click', onClick, { capture: true }),
    );
  }

  private isBehindDialog(host: HTMLElement): boolean {
    return (
      !host.closest('.cdk-dialog-container') && !!document.querySelector('.cdk-dialog-container')
    );
  }

  private isEditingText(event: KeyboardEvent): boolean {
    const target: HTMLElement | null = event.target as HTMLElement | null;
    return !!target?.closest(
      'input, textarea, select, [contenteditable]:not([contenteditable="false"])',
    );
  }

  moveSelection(position: HorizontalPosition): void {
    if (position === 'left') {
      this.moveSelectionLeft();
      return;
    }
    this.moveSelectionRight();
  }

  readonly totalDuration: Signal<string> = computed<string>(() =>
    formatShortDuration(
      this.sections().reduce(
        (total: number, section: SectionModelType) => total + durationToSeconds(section.duration),
        0,
      ),
    ),
  );

  /**
   * Min, moyenne et max de chaque champ du mode, tous segments confondus : une
   * ligne par valeur (deux pour `incSpd`), l'incline et la vitesse ne se melangent pas.
   *
   * On passe par `sectionDatas` car la clef stockee dans `data` n'est pas le nom
   * du mode (`res` -> `resistance`, `inc` -> `incline`, `spd` -> `speed`).
   */
  readonly dataStats: Signal<DataStat[]> = computed(() => {
    const secondaryField: { type: WorkoutMode } | undefined =
      this.sectionEditorService.secondaryField();
    const fields: { type: WorkoutMode }[] = [
      this.sectionEditorService.primaryField(),
      ...(secondaryField ? [secondaryField] : []),
    ];
    return fields.map(({ type }: { type: WorkoutMode }, index: number) => {
      const values: number[] = this.sections().map(
        (section: SectionModelType) =>
          this.sectionEditorService.sectionDatas(section, this.workoutMode())[index].value,
      );
      return { type, min: minOf(values), avg: avgOf(values), max: maxOf(values) };
    });
  });

  readonly dataStatMetrics: readonly { key: 'min' | 'avg' | 'max'; label: string }[] = [
    { key: 'min', label: 'sections_editor.metrics.min' },
    { key: 'avg', label: 'sections_editor.metrics.avg' },
    { key: 'max', label: 'sections_editor.metrics.max' },
  ];

  formatDataStat(value: number | undefined, type: WorkoutMode): string {
    return value != null ? `${value} ${this.sectionEditorService.getWorkoutModeUnit(type)}` : '-';
  }

  /**
   * Sections selectionnees, dans l'ordre du workout.
   *
   * Derive des ids plutot que stocke : chaque modification remplace la section
   * par une copie, une liste stockee garderait les anciennes valeurs.
   */
  readonly selectedSections: Signal<SectionModelType[]> = computed(() => {
    const selectedIds: number[] = this.sectionEditorService.selectedSectionsIds();
    return this.sections().filter((section: SectionModelType) => selectedIds.includes(section.id));
  });

  public deleteSection(section: SectionModelType): void {
    if (this.selectedSections().length === 0) {
      this.deleteUniqueSection(section);
      return;
    }
    this.deleteSelectedSections();
  }

  private deleteUniqueSection(section: SectionModelType): void {
    this.sections.update(() =>
      this.sections().filter((_section: SectionModelType) => section.id !== _section.id),
    );
  }

  /** Supprime les sections selectionnees et vide la selection. */
  deleteSelectedSections(): void {
    const selectedIds: number[] = this.sectionEditorService.selectedSectionsIds();
    this.sections.set(
      this.sections().filter((section: SectionModelType) => !selectedIds.includes(section.id)),
    );
    this.sectionEditorService.clearSectionSelection();
  }

  /**
   * Chaque section selectionnee echange sa place avec sa voisine de droite si
   * celle-ci n'est pas selectionnee. On parcourt de droite a gauche pour qu'un
   * groupe de sections contigues avance d'un cran d'un seul bloc.
   * Une section deja au bord (ou bloquee par une selectionnee au bord) ne bouge pas.
   */
  private moveSelectionRight(): void {
    const selectedIds: number[] = this.sectionEditorService.selectedSectionsIds();
    const reordered: SectionModelType[] = [...this.sections()];
    for (let index = reordered.length - 2; index >= 0; index--) {
      if (
        selectedIds.includes(reordered[index].id) &&
        !selectedIds.includes(reordered[index + 1].id)
      ) {
        moveItemInArray(reordered, index, index + 1);
      }
    }
    this.sections.set(reordered);
  }

  /** Symetrique de `moveSelectionRight`, en parcourant de gauche a droite. */
  private moveSelectionLeft(): void {
    const selectedIds: number[] = this.sectionEditorService.selectedSectionsIds();
    const reordered: SectionModelType[] = [...this.sections()];
    for (let index = 1; index < reordered.length; index++) {
      if (
        selectedIds.includes(reordered[index].id) &&
        !selectedIds.includes(reordered[index - 1].id)
      ) {
        moveItemInArray(reordered, index, index - 1);
      }
    }
    this.sections.set(reordered);
  }

  drop(event: CdkDragDrop<unknown>) {
    // moveItemInArray mute le tableau : on reordonne une copie, sinon la
    // reference ne change pas et le signal ne notifie personne.
    const reordered: SectionModelType[] = [...this.sections()];
    moveItemInArray(reordered, event.previousIndex, event.currentIndex);
    this.sections.set(reordered);
  }

  updateSections(sections: SectionModelType[]) {
    this.sections.set(sections);
  }

  /**
   * Sections au debut du resize en cours, `null` hors resize.
   *
   * Pendant un resize, chaque delta est cumule depuis le debut du geste et
   * applique a cette photo : une section bloquee sur une borne retrouve son
   * ecart si on revient en arriere dans le meme geste.
   */
  private changeBase: SectionModelType[] | null = null;

  startSectionChange(): void {
    this.changeBase = this.sections();
  }

  endSectionChange(): void {
    this.changeBase = null;
  }

  /**
   * Applique un changement a la section source, ou a toute la selection si la
   * section source en fait partie.
   */
  applySectionChange(sourceId: number, change: SectionChange): void {
    const selectedIds: number[] = this.sectionEditorService.selectedSectionsIds();
    const targetIds: number[] = selectedIds.includes(sourceId) ? selectedIds : [sourceId];
    const base: SectionModelType[] = this.changeBase ?? this.sections();
    this.sections.set(
      base.map((section: SectionModelType) =>
        targetIds.includes(section.id)
          ? this.sectionEditorService.applyChange(section, change, this.workoutMode())
          : section,
      ),
    );
  }

  public duplicateSection(section: SectionModelType): void {
    if (this.selectedSections().length === 0) {
      this.duplicateUniqueSection(section);
      return;
    }
    this.duplicateSelectionOfSections();
  }

  public createNewSection(): void {
    if (this.workoutMode()) {
      this.createInitialWorkoutSection(this.workoutMode()!);
    } else {
      this.createInitialCoachingSection();
    }
  }

  /**
   * Crée une section **à droite** de la section à dupliquer.
   */
  private duplicateUniqueSection(section: SectionModelType): void {
    const sectionIndex: number = this.sections().findIndex(
      (s: SectionModelType) => section.id === s.id,
    );
    const updatedSection: SectionModelType = { ...section, id: generateSectionId() };
    this.sections.set([
      ...this.sections().slice(0, sectionIndex),
      updatedSection,
      ...this.sections().slice(sectionIndex, this.sections().length),
    ]);
  }

  /**
   * Duplique les sections selectionnees
   * et les ajoutes **à la fin du workout**
   */
  public duplicateSelectionOfSections(): void {
    const duplicatedSections: SectionModelType[] = this.selectedSections().map(
      (s: SectionModelType) => ({ ...s, id: generateSectionId() }),
    );
    this.sections.set([...this.sections(), ...duplicatedSections]);
  }

  private createInitialWorkoutSection(mode: WorkoutMode): void {
    const section: SectionModelType = createDefaultWorkoutSection(mode);
    this.sections.update(() => [...this.sections(), section]);
  }

  private createInitialCoachingSection(): void {
    const section: SectionModelType = createDefaultCoachingSection();
    this.sections.update(() => [...this.sections(), section]);
  }
}
